// src/publicuser/components/PostContent.jsx
import React, { useState, useEffect } from 'react';
import '../../css/PostContent.css';
import { postService } from '../../../services/postService'; 
import ContentBlock from '../../../components/ContentBlock';
import defaultProfile from '../../../img/akash.jpg';

const PostContent = ({ 
  postId, 
  onNext, 
  onPrevious, 
  hasNext, 
  hasPrevious,
  currentPostTitle,
  nextPostTitle,
  previousPostTitle 
}) => {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const loadPostDetails = async () => {
      if (!postId) return;
      
      setIsLoading(true);
      try {
        const response = await postService.getPostWithContent(postId);
        setPost(response.data);
      } catch (error) {
        console.error('Failed to load post details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPostDetails();
  }, [postId]);

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;
      setReadingProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="learn-post-loading">
        <div className="learn-loading-spinner">
          <div className="learn-spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="learn-post-error">
        <div className="learn-error-content">
          <span className="learn-error-icon">📖</span>
          <h3>Content Not Found</h3>
          <p>Unable to load the tutorial. Please try again.</p>
        </div>
      </div>
    );
  }

  // Extract short title
  const shortTitle = post.shortTitle || post.title;

  return (
    <>
      {/* Reading Progress Bar */}
      <div className="learn-progress-bar">
        <div 
          className="learn-progress-fill" 
          style={{ width: `${readingProgress}%` }}
        ></div>
      </div>

      <article className="learn-post-article">
        {/* Minimal Header with Short Title */}
        <div className="learn-post-minimal-header">
          <div className="learn-post-badge">
            <span className="learn-badge-icon">✨</span>
            <span>Tutorial</span>
          </div>
          <h1 className="learn-post-short-title">{shortTitle}</h1>
        </div>

        {/* Author Strip - Clean & Minimal */}
        <div className="learn-author-strip">
          <div className="learn-author-info">
            <div className="learn-author-avatar-wrapper">
              <img 
                // src={post.userProfile || defaultProfile} 
                src={defaultProfile} 
                alt={post.userName || 'Author'} 
                className="learn-author-avatar"
                onError={(e) => {
                  e.target.src = defaultProfile;
                }}
              />
              <div className="learn-author-status"></div>
            </div>
            <div className="learn-author-meta">
              <span className="learn-author-name">{post.userName || 'Tech Shara Team'}</span>
              <div className="learn-post-stats">
                <span className="learn-stat">
                  <span className="learn-stat-icon">📅</span>
                  {new Date(post.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
                <span className="learn-stat-divider">•</span>
                <span className="learn-stat">
                  <span className="learn-stat-icon">👁️</span>
                  {post.views || 0} views
                </span>
                <span className="learn-stat-divider">•</span>
                <span className="learn-stat">
                  <span className="learn-stat-icon">⏱️</span>
                  {Math.ceil((post.contentBlock?.length || 0) * 0.5)} min read
                </span>
              </div>
            </div>
          </div>
          
          {/* Share Button */}
          <button 
            className="learn-share-btn" 
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: post.title,
                  text: post.description,
                  url: window.location.href
                });
              } else {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
              }
            }}
          >
            <span>📤</span> Share
          </button>
        </div>

        {/* Elegant Description Line */}
        {post.description && (
          <div className="learn-post-description">
            <div className="learn-description-quote">
              {/* <span className="learn-quote-mark">"</span> */}
              <p>{post.description}</p>
              {/* <span className="learn-quote-mark">"</span> */}
            </div>
          </div>
        )}
        
        {/* Content Blocks - Simple Layout */}
        <div className="learn-content-simple">
          {Array.isArray(post.contentBlock) && post.contentBlock.length > 0 ? (
            post.contentBlock
              .sort((a, b) => a.order - b.order)
              .map((block) => (
                <div key={block.id} className="learn-content-block-wrapper">
                  <ContentBlock block={block} />
                </div>
              ))
          ) : (
            <div className="learn-placeholder-content">
              <div className="learn-placeholder-wave">
                <div className="learn-wave-bar"></div>
                <div className="learn-wave-bar"></div>
                <div className="learn-wave-bar"></div>
                <div className="learn-wave-bar"></div>
              </div>
              <p>Content is being prepared...</p>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="learn-post-navigation">
          <div className="learn-nav-container">
            <button 
              className={`learn-nav-prev ${!hasPrevious ? 'learn-disabled' : ''}`}
              onClick={onPrevious}
              disabled={!hasPrevious}
            >
              <span className="learn-nav-icon">←</span>
              <div className="learn-nav-text">
                <span className="learn-nav-label">Previous</span>
                <span className="learn-nav-title">{previousPostTitle || 'Previous Tutorial'}</span>
              </div>
            </button>
            
            <div className="learn-nav-center">
              <div className="learn-nav-progress">
                <span className="learn-progress-text">
                  {hasNext ? 'Continue Learning' : 'Complete'}
                </span>
              </div>
            </div>
            
            <button 
              className={`learn-nav-next ${!hasNext ? 'learn-disabled' : ''}`}
              onClick={onNext}
              disabled={!hasNext}
            >
              <div className="learn-nav-text">
                <span className="learn-nav-label">Next</span>
                <span className="learn-nav-title">{nextPostTitle || 'Next Tutorial'}</span>
              </div>
              <span className="learn-nav-icon">→</span>
            </button>
          </div>
        </div>
      </article>
    </>
  );
};

export default PostContent;